import { useEffect, useMemo, useState } from "react";
import L from "leaflet";
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from "react-leaflet";
import { api, type APIResult } from "../../api/client";
import { useAppState } from "../../hooks/useAppState";
import { bboxFromLines, resultToRouteLines, type RouteLine } from "../../lib/extractRoute";
import { classNames } from "../../lib/format";
import { PageHeader } from "../PageHeader";
import { ResponseCard } from "../ResponseCard";
import { SectionCard } from "../SectionCard";
import type { GeocodeResponse, GeocodeResult, ResultResponse } from "../../types/routecore";

// Tab de mapa: carrega o resultado do job (cache no AppState) e desenha
// as rotas em camadas Polyline. Também oferece um campo de busca
// (geocoding) que abre o local clicado, similar ao Google Maps.

// Cor para cada rota — ciclo de 8 cores diferentes para legibilidade.
const ROUTE_COLORS = [
  "#dc2626",
  "#ea580c",
  "#ca8a04",
  "#16a34a",
  "#0891b2",
  "#2563eb",
  "#7c3aed",
  "#db2777",
];

// URL do template de tiles raster. Default: caminho RELATIVO servido pela
// própria plataforma — em produção o nginx do kitchen-sink faz proxy de
// /tiles para o TileServer GL na rede interna; em dev o Vite proxia para o
// tileserver publicado no host (vite.config.ts). Sobrescreva via
// VITE_TILESERVER_URL apenas para usar um provider externo.
// `||` (não `??`): o build-arg do Docker pode chegar como string vazia.
const TILE_URL =
  (import.meta.env.VITE_TILESERVER_URL as string | undefined) ||
  "/tiles/styles/liberty/{z}/{x}/{y}.png";

const TILE_ATTRIBUTION =
  '&copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap</a> contributors';

// Ícone de parada — desenhado com SVG inline (sem precisar de assets).
function makeIcon(color: string, label: string | number) {
  return L.divIcon({
    className: "ks-stop-icon",
    html: `
      <div style="
        width:24px;height:24px;border-radius:9999px;
        background:${color};color:white;font:600 11px ui-sans-serif,system-ui;
        display:grid;place-content:center;border:2px solid white;
        box-shadow:0 1px 4px rgba(0,0,0,.4);
      ">${label}</div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
}

// Ícone do marcador de busca — pin estilo "ponto de interesse".
function makeSearchIcon() {
  return L.divIcon({
    className: "ks-search-icon",
    html: `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="40"
           style="filter:drop-shadow(0 2px 4px rgba(0,0,0,.45));">
        <path d="M12 2 C8 2 5 5 5 9 C5 14 12 22 12 22 C12 22 19 14 19 9 C19 5 16 2 12 2 Z"
              fill="#2563eb" stroke="white" stroke-width="2"/>
        <circle cx="12" cy="9" r="2.5" fill="white"/>
      </svg>
    `,
    iconSize: [32, 40],
    iconAnchor: [16, 38],
    popupAnchor: [0, -34],
  });
}

function FitBounds({ lines }: { lines: RouteLine[] }) {
  const map = useMap();
  useEffect(() => {
    const bbox = bboxFromLines(lines);
    if (!bbox) return;
    const [min, max] = bbox;
    map.fitBounds(
      [
        [min.lat, min.lon],
        [max.lat, max.lon],
      ],
      { padding: [40, 40] },
    );
  }, [lines, map]);
  return null;
}

// FlyTo dispara um voo suave quando o target muda. Usa key composto
// para reagir mesmo quando o usuário clica de novo no mesmo lugar
// (força o efeito a rodar).
function FlyTo({ target }: { target: { lat: number; lon: number; nonce: number } | null }) {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    map.flyTo([target.lat, target.lon], 16, { duration: 0.9 });
  }, [target, map]);
  return null;
}

type PickedPlace = {
  lat: number;
  lon: number;
  label: string;
  placeType?: string;
  confidence?: number;
  // nonce serve para o FlyTo reagir mesmo quando o usuário reclica
  // no mesmo resultado.
  nonce: number;
};

// regex permissivo para `lat,lon` ou `lat lon`. Aceita decimal com `.`.
const LATLON_PATTERN = /^\s*(-?\d{1,3}(?:\.\d+)?)\s*[,\s]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/;

export function TabMap() {
  const { trackedJobId } = useAppState();
  const [input, setInput] = useState(trackedJobId ?? "");
  const [result, setResult] = useState<APIResult<ResultResponse> | null>(null);
  const [loading, setLoading] = useState(false);

  // Estado da busca por local.
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<GeocodeResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchCached, setSearchCached] = useState(false);
  const [picked, setPicked] = useState<PickedPlace | null>(null);

  useEffect(() => {
    setInput(trackedJobId ?? "");
  }, [trackedJobId]);

  async function fetchResult() {
    if (!input) return;
    setLoading(true);
    setResult(null);
    const r = await api.getJobResult(input.trim());
    setResult(r as APIResult<ResultResponse>);
    setLoading(false);
  }

  function pickResult(r: GeocodeResult) {
    setPicked({
      lat: r.location.lat,
      lon: r.location.lon,
      label: r.formatted_address,
      placeType: r.place_type,
      confidence: r.confidence,
      nonce: Date.now(),
    });
  }

  async function handleSearch() {
    const q = searchQuery.trim();
    if (!q) return;

    // Atalho: se o usuário digitou "lat, lon", pula o geocode e abre
    // direto no mapa. Útil para colar coords de outras telas/tabs.
    const m = LATLON_PATTERN.exec(q);
    if (m) {
      const lat = parseFloat(m[1]);
      const lon = parseFloat(m[2]);
      if (
        Number.isFinite(lat) &&
        Number.isFinite(lon) &&
        Math.abs(lat) <= 90 &&
        Math.abs(lon) <= 180
      ) {
        const item: GeocodeResult = {
          formatted_address: `${lat.toFixed(5)}, ${lon.toFixed(5)}`,
          location: { lat, lon },
          confidence: 1,
          place_type: "coords",
        };
        setSearchError(null);
        setSearchCached(false);
        setSearchResults([item]);
        pickResult(item);
        return;
      }
    }

    setSearchLoading(true);
    setSearchError(null);
    setSearchResults([]);
    const r = await api.geocode({ address: q, country_code: "BR", limit: 8 });
    setSearchLoading(false);

    if (!r.ok) {
      const body = r.data as
        | { message?: string; error?: { message?: string; code?: string } }
        | null;
      const upstream = body?.error?.message ?? body?.message;
      const hint =
        r.status === 502
          ? "Nominatim indisponível — verifique se o container `routecore-nominatim` está no ar."
          : `Erro HTTP ${r.status}`;
      setSearchError(upstream ? `${hint} (${upstream})` : hint);
      return;
    }
    const data = r.data as GeocodeResponse | null;
    setSearchCached(data?.cached ?? false);
    setSearchResults(data?.results ?? []);
    if (!data || data.results.length === 0) {
      setSearchError("Nenhum resultado encontrado.");
    }
  }

  function clearSearch() {
    setSearchQuery("");
    setSearchResults([]);
    setSearchError(null);
    setSearchCached(false);
    setPicked(null);
  }

  const lines = useMemo(() => resultToRouteLines(result?.data), [result]);
  const fallbackCenter: [number, number] = [-27.0996, -52.6188]; // Chapecó - SC

  return (
    <div className="space-y-4">
      <PageHeader
        title="Mapa interativo"
        subtitle="Busca de local e renderização das rotas decodificadas (polyline ou GeoJSON) sobre o TileServer GL."
      />

      <SectionCard
        title="Buscar local"
        description="Aceita endereço, ponto de interesse ou `lat, lon`. Os resultados vêm do Nominatim (cache Redis no upstream)."
        actions={
          (picked || searchResults.length > 0) && (
            <button type="button" className="ks-btn-ghost text-xs" onClick={clearSearch}>
              limpar
            </button>
          )
        }
      >
        <div className="flex gap-2">
          <div className="relative flex-1">
            <svg
              viewBox="0 0 24 24"
              className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-ink-500"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="text"
              className="ks-input pl-8"
              placeholder="Av. Getúlio Vargas, Chapecó  —  ou  -27.0996, -52.6188"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void handleSearch();
                }
              }}
            />
          </div>
          <button
            type="button"
            className="ks-btn-primary text-xs"
            onClick={handleSearch}
            disabled={searchLoading || !searchQuery.trim()}
          >
            {searchLoading ? "buscando..." : "Buscar"}
          </button>
        </div>

        {searchError && (
          <p className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            {searchError}
          </p>
        )}

        {searchResults.length > 0 && (
          <ul className="ks-scroll mt-3 max-h-64 overflow-auto divide-y divide-ink-200 rounded-md border border-ink-200 bg-white">
            {searchResults.map((r, i) => {
              const isActive =
                picked &&
                Math.abs(picked.lat - r.location.lat) < 1e-9 &&
                Math.abs(picked.lon - r.location.lon) < 1e-9;
              return (
                <li key={`${r.location.lat}-${r.location.lon}-${i}`}>
                  <button
                    type="button"
                    onClick={() => pickResult(r)}
                    className={classNames(
                      "flex w-full items-start gap-2 px-3 py-2 text-left text-sm transition hover:bg-ink-50",
                      isActive && "bg-brand-50",
                    )}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      className={classNames(
                        "mt-0.5 size-4 shrink-0",
                        isActive ? "text-brand-700" : "text-ink-500",
                      )}
                      fill="currentColor"
                      stroke="white"
                      strokeWidth="1.5"
                    >
                      <path d="M12 2 C8 2 5 5 5 9 C5 14 12 22 12 22 C12 22 19 14 19 9 C19 5 16 2 12 2 Z" />
                      <circle cx="12" cy="9" r="2.2" fill="white" stroke="none" />
                    </svg>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-ink-900">
                        {r.formatted_address}
                      </p>
                      <p className="font-mono text-[11px] text-ink-500">
                        {r.location.lat.toFixed(5)}, {r.location.lon.toFixed(5)}
                        {r.place_type ? ` · ${r.place_type}` : ""}
                        {Number.isFinite(r.confidence)
                          ? ` · ${(r.confidence * 100).toFixed(0)}%`
                          : ""}
                      </p>
                    </div>
                    {isActive && (
                      <span className="ks-tag bg-brand-100 text-brand-800">no mapa</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {(searchCached || picked) && (
          <p className="mt-2 text-[10px] text-ink-500">
            {searchCached && <span>resposta servida do cache Redis upstream · </span>}
            {picked && (
              <span>
                local ativo:{" "}
                <span className="ks-code">
                  {picked.lat.toFixed(5)}, {picked.lon.toFixed(5)}
                </span>
              </span>
            )}
          </p>
        )}
      </SectionCard>

      <SectionCard
        title="Job"
        description="Cole um job_id em estado terminal com solução para desenhar as rotas no mapa."
      >
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            className="ks-input ks-code flex-1 min-w-[280px]"
            placeholder="job_id (UUID)"
            value={input}
            onChange={(e) => setInput(e.target.value.trim())}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void fetchResult();
              }
            }}
          />
          <button
            type="button"
            className="ks-btn-primary text-xs"
            disabled={loading || !input}
            onClick={fetchResult}
          >
            {loading ? "carregando..." : "Carregar resultado"}
          </button>
        </div>
        <p className="mt-2 text-[11px] text-ink-500">
          Tiles servidos por <code className="ks-code">{TILE_URL}</code> — o default é o
          TileServer GL da própria plataforma, via proxy de mesma origem. Para usar um
          provider externo, sobrescreva <code className="ks-code">VITE_TILESERVER_URL</code>{" "}
          (<code className="ks-code">frontend/.env</code> em dev; build-arg em produção).
        </p>
      </SectionCard>

      {lines.length > 0 && (
        <SectionCard title="Legenda">
          <ul className="flex flex-wrap gap-3">
            {lines.map((l, i) => (
              <li key={l.routeId} className="flex items-center gap-2 text-xs text-ink-700">
                <span
                  className="size-3 rounded-full"
                  style={{ background: ROUTE_COLORS[i % ROUTE_COLORS.length] }}
                />
                <span>
                  <span className="font-semibold text-ink-900">{l.driver}</span>{" "}
                  <span className="text-ink-500">({l.vehicle})</span> · {l.stops.length} paradas
                </span>
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      <div className="ks-card overflow-hidden">
        <div className="h-[60vh] min-h-[420px] w-full">
          <MapContainer
            center={fallbackCenter}
            zoom={12}
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom
          >
            <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
            {lines.map((line, i) => {
              const color = ROUTE_COLORS[i % ROUTE_COLORS.length];
              return <RouteLayer key={line.routeId} line={line} color={color} index={i} />;
            })}
            {lines.length > 0 && <FitBounds lines={lines} />}
            {picked && (
              <Marker position={[picked.lat, picked.lon]} icon={makeSearchIcon()}>
                <Popup>
                  <div className="text-sm">
                    <p className="font-semibold">{picked.label}</p>
                    <p className="font-mono text-[11px] text-ink-500">
                      {picked.lat.toFixed(5)}, {picked.lon.toFixed(5)}
                    </p>
                    {picked.placeType && (
                      <p className="text-[11px] text-ink-500">{picked.placeType}</p>
                    )}
                  </div>
                </Popup>
              </Marker>
            )}
            {picked && <FlyTo target={picked} />}
          </MapContainer>
        </div>
      </div>

      <ResponseCard
        result={result}
        loading={loading}
        emptyHint="Carregue o resultado de um job terminal para ver as rotas desenhadas."
      />
    </div>
  );
}

function RouteLayer({
  line,
  color,
  index,
}: {
  line: RouteLine;
  color: string;
  index: number;
}) {
  return (
    <>
      <Polyline
        positions={line.positions}
        pathOptions={{ color, weight: 4, opacity: 0.85 }}
      />
      {line.stops.map((stop) => (
        <Marker
          key={`${index}-${stop.sequence}`}
          position={stop.position}
          icon={makeIcon(color, stop.sequence)}
        >
          <Popup>
            <div className="text-sm">
              <p className="font-semibold">
                Parada {stop.sequence} · {stop.type}
              </p>
              {stop.orderId && (
                <p>
                  <span className="text-ink-500">order:</span> <code>{stop.orderId}</code>
                </p>
              )}
              <p>
                <span className="text-ink-500">ETA:</span>{" "}
                {new Date(stop.eta).toLocaleString("pt-BR", { hour12: false })}
              </p>
              <p className="text-[11px] text-ink-500">
                {line.driver} · {line.vehicle}
              </p>
            </div>
          </Popup>
        </Marker>
      ))}
    </>
  );
}
